import React from 'react';
import Tooltip from '../components/Tooltip.jsx';
import {FormattedMessage, useIntl} from 'react-intl';
import {formatCurrency} from './format.mjs';
import {getMessageValue} from '../i18n/messageUtil.mjs';
import formatNotificationValues from '../validation/formatNotificationValues.mjs';
import {useText} from '../components/TextContext';
import MessageIfPresent from '../components/MessageIfPresent';

/* eslint-disable react/display-name */
const htmlFormatValues = {
  b: (...chunks) => <b>{chunks}</b>,
  br: <br />,
  del: (...chunks) => <del>{chunks}</del>,
  em: (...chunks) => <em>{chunks}</em>,
  h1: (...chunks) => <h1>{chunks}</h1>,
  h2: (...chunks) => <h2>{chunks}</h2>,
  h3: (...chunks) => <h3>{chunks}</h3>,
  h4: (...chunks) => <h4>{chunks}</h4>,
  h5: (...chunks) => <h5>{chunks}</h5>,
  i: (...chunks) => <i>{chunks}</i>,
  ins: (...chunks) => <ins>{chunks}</ins>,
  li: (...chunks) => <li>{chunks}</li>,
  mark: (...chunks) => <mark>{chunks}</mark>,
  small: (...chunks) => <small>{chunks}</small>,
  strong: (...chunks) => <strong>{chunks}</strong>,
  sub: (...chunks) => <sub>{chunks}</sub>,
  sup: (...chunks) => <sup>{chunks}</sup>,
  ul: (...chunks) => <ul>{chunks}</ul>,
  value: '',
};
/* eslint-enable react/display-name */

const getQuestionAnswersArray = (question, customTag = 'span') => {
  return (question.answers || []).map(answer =>
    Object.assign(
      {},
      {
        key: `${question.appkey}.${answer.key}`,
        label: getQuestionAnswerText(question.appkey, answer.key, customTag),
        value: answer.key,
        tooltip: getQuestionAnswerTooltip(question.appkey, answer.key, answer?.attributes),
      }
    )
  );
};

const getQuestionAnswerText = (questionKey, answerKey, customTag = 'span') => {
  const intl = useIntl();
  let messageKey = `${questionKey}.${answerKey}`;
  const textContext = useText();

  return (
    textContext?.[`${messageKey}`] || intl.formatMessage({id: messageKey, tagName: customTag}, {...htmlFormatValues})
  );
};

const getQuestionAnswerTooltip = (questionKey, answerKey, attributes) => {
  const messageKey = `${questionKey}.${answerKey}.tooltip`;
  const tooltipProps = attributes?.tooltipProps || {};
  const textContext = useText();
  return (
    textContext?.[`${messageKey}`] || (
      <FormattedMessage id={messageKey} defaultMessage={messageKey}>
        {message =>
          message === messageKey ? null : (
            <Tooltip className="answer__tooltip" messageId={messageKey} attributes={tooltipProps} />
          )
        }
      </FormattedMessage>
    )
  );
};

const gt = (answers, appkey, amount) => op['>'](answers, appkey, amount);

const lt = (answers, appkey, amount) => op['<'](answers, appkey, amount);

const op = {
  '<': (answers, appkey, amount) =>
    Object.prototype.hasOwnProperty.call(answers, appkey) ? answers[appkey].value < amount : false,
  '<=': (answers, appkey, amount) =>
    Object.prototype.hasOwnProperty.call(answers, appkey) ? answers[appkey].value <= amount : false,
  '>': (answers, appkey, amount) =>
    Object.prototype.hasOwnProperty.call(answers, appkey) ? answers[appkey].value > amount : false,
  '>=': (answers, appkey, amount) =>
    Object.prototype.hasOwnProperty.call(answers, appkey) ? answers[appkey].value >= amount : false,
  '=': (answers, appkey, amount, defaultValue = false) => {
    const value = Object.prototype.hasOwnProperty.call(answers, appkey) ? answers[appkey].value : defaultValue;
    return value === amount;
  },
};

const parseAnswerValue = (intl, {appkey, value, type = '', view = {}}, details = false) => {
  const attributes = view.attributes || {};
  const questionType = view.type ? view.type.toLowerCase() : '';
  const dataType = (attributes.dataType || type).toLowerCase();
  const textContext = useText();
  const id =
    details && getMessageValue(intl.locale, `details.${appkey}.${value}`)
      ? `details.${appkey}.${value}`
      : `${appkey}.${value}`;
  let result = '';

  const formatNumberQuestionValue = (format, value) => {
    switch (format) {
      case '%':
      case 'percent':
        return value || value === 0 ? `${value}%` : '';
      case '$':
      case 'money':
      case 'currency':
        return value || value === 0 ? formatCurrency(value) : '';
      default:
        return value;
    }
  };

  const formatTextQuestionValue = () => {
    if (questionType === 'textinput') {
      return Array.isArray(value) ? value[0] : value;
    } else if (Array.isArray(value)) {
      const answerTextMap = value.reduce((agg, v) => {
        const id =
          details && getMessageValue(intl.locale, `details.${appkey}.${v}`)
            ? `details.${appkey}.${v}`
            : `${appkey}.${v}`;
        agg = agg.concat(
          textContext?.[`${id}`] ||
          <FormattedMessage key={id} id={id} values={htmlFormatValues} />, ', '
        );
        return agg;
      }, []);

      return answerTextMap.splice(0, answerTextMap.length - 1);
    } else {
      return value ? textContext?.[`${id}`] || intl.formatMessage({id}, {...htmlFormatValues}) : null;
    }
  };

  switch (dataType) {
    case 'number':
      result = textContext?.[`${id}`] || formatNumberQuestionValue(attributes.format, value);
      break;
    case 'boolean':
      result = textContext?.[`${id}`] || intl.formatMessage({id});
      break;
    default:
      result = formatTextQuestionValue();
  }

  return result;
};

// Cannot use textContext here because this function is currently passed to provide content prop to tooltip which already uses textContext
const renderQuestionErrors = error => {
  return Object.keys(error.errors).map((type, index) => {
    let values = error.errors[type];
    const validationType = type === 'between' && values.length > 1 && values[0] === values[1] ? 'equal' : type;
    values = error?.attributes?.format ? formatNotificationValues(error.attributes.format, values) : values;
    return (
      <MessageIfPresent id={`error.question.${validationType}`} key={`${type}-${index}`}>
        <FormattedMessage key={`error-${index}`} id={`error.question.${validationType}`} values={{...values}} />
      </MessageIfPresent>
    );
  });
};

export {
  getQuestionAnswersArray,
  getQuestionAnswerText,
  getQuestionAnswerTooltip,
  gt,
  htmlFormatValues,
  lt,
  op,
  parseAnswerValue,
  renderQuestionErrors,
};
